/* Sell-Invoice Modal (Phase SC-1)
 *
 * Opens from SellerInvoices when the seller clicks "Get paid today" on a
 * confirmed invoice with Net-X terms. Reads eligibility from the server,
 * shows the per-invoice fee breakdown, and either advances or shows the
 * exact reasons the invoice can't be sold.
 */
import { useEffect, useState } from "react";
import api, { money } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, XCircle, ShieldCheck, Zap } from "lucide-react";

const CHECK_LABELS = {
  feature_enabled:            "Feature enabled",
  seller_credit_line_active:  "You have an active credit line",
  invoice_not_terminal:       "Invoice is still open",
  not_already_sold:           "Invoice not already sold",
  buyer_accepted_terms:       "Buyer accepted Net-X terms",
  no_open_disputes:           "No open disputes",
  amount_positive:            "Invoice amount > $0",
  within_credit_limit:        "Within your credit limit",
  buyer_has_cover:            "Buyer is covered by Allianz (lender-side)",
  carrier_movement_confirmed: "Carrier confirmed goods are in transit",
};

export default function SellInvoiceModal({ invoice, open, onClose, onSuccess }) {
  const [elig, setElig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !invoice) { setElig(null); return; }
    setLoading(true);
    api.get(`/seller/invoices/${invoice.id}/purchase-eligibility`)
      .then(r => setElig(r.data))
      .catch(e => {
        toast.error(e?.response?.data?.detail?.message || "Could not load eligibility");
        onClose();
      })
      .finally(() => setLoading(false));
  }, [open, invoice]);

  const sell = async () => {
    if (!invoice) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/seller/invoices/${invoice.id}/purchase`);
      toast.success(`${money(data.advance_amount)} advanced to your wallet · purchase ${data.purchase_id.slice(0, 8)}`);
      onSuccess?.(data);
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail || e?.response?.data;
      const reason = d?.reason || "Sale failed";
      toast.error(d?.message || reason);
    } finally {
      setBusy(false);
    }
  };

  const pricing = elig?.pricing;

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent data-testid="sell-invoice-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="display text-xl tracking-tight flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-lime-600"/>
            Get paid today · {invoice?.code}
          </DialogTitle>
          <DialogDescription>
            Sell this invoice to your lender · receive {pricing?.advance_rate ? (pricing.advance_rate*100).toFixed(0) : 95}% upfront ·
            buyer still gets full Net-X terms.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-8 text-center text-sm text-foreground/55">Checking eligibility…</div>}

        {elig && (
          <div className="space-y-4 py-2" data-testid="sell-invoice-eligibility">
            {/* Eligibility checks */}
            <div className="rounded-xl border border-border bg-secondary/40 p-3 space-y-1.5">
              {elig.checks.map(c => (
                <div key={c.key} className="flex items-start gap-2 text-[12.5px]">
                  {c.ok
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0"/>
                    : <XCircle className="w-3.5 h-3.5 text-rose-600 mt-0.5 shrink-0"/>}
                  <span className={c.ok ? "text-foreground/70" : "text-rose-800 font-medium"}>
                    {CHECK_LABELS[c.key] || c.key}
                    {!c.ok && c.detail && <span className="text-foreground/55 font-normal"> · {c.detail}</span>}
                  </span>
                </div>
              ))}
            </div>

            {/* Pricing */}
            {pricing && (
              <div className="rounded-xl border border-lime-300 bg-lime-50 p-4 space-y-1.5 text-[13px]" data-testid="sell-invoice-pricing">
                <div className="flex justify-between"><span className="text-foreground/65">Invoice face</span><span className="mono tabular-nums">{money(elig.amount_usd)}</span></div>
                {/* Phase SC-Terms · Show buyer terms tied to seller offer */}
                {elig.terms && (
                  <div className="flex justify-between" data-testid="sell-invoice-terms-row">
                    <span className="text-foreground/65">
                      Buyer terms{elig.seller_offered_terms === elig.terms ? " (as you offered)" : (elig.seller_offered_terms ? ` · offered ${elig.seller_offered_terms.toUpperCase()}` : "")}
                    </span>
                    <span className="mono tabular-nums">{elig.terms.toUpperCase()}</span>
                  </div>
                )}
                <div className="flex justify-between"><span className="text-foreground/65">Advance ({(pricing.advance_rate*100).toFixed(0)}%)</span><span className="mono tabular-nums">{money(pricing.advance_amount)}</span></div>
                <div className="flex justify-between"><span className="text-foreground/65">Reserve (paid when buyer pays)</span><span className="mono tabular-nums">{money(pricing.reserve_amount)}</span></div>
                <div className="flex justify-between"><span className="text-foreground/65">Discount fee ({(pricing.fee_pct*100).toFixed(2)}%)</span><span className="mono tabular-nums">−{money(pricing.fee_usd)}</span></div>
                <div className="border-t border-lime-200 pt-1.5 flex justify-between font-semibold">
                  <span>Your net</span><span className="mono tabular-nums">{money(pricing.seller_net_usd)}</span>
                </div>
              </div>
            )}

            {/* Cover disclosure */}
            <div className={`flex items-start gap-2 rounded-lg p-3 text-[12px] ${
              elig.cover.covered
                ? "bg-emerald-50 border border-emerald-200 text-emerald-900"
                : "bg-amber-50 border border-amber-200 text-amber-900"
            }`}>
              <ShieldCheck className={`w-4 h-4 mt-0.5 shrink-0 ${elig.cover.covered ? "text-emerald-700" : "text-amber-700"}`}/>
              <div>
                {elig.cover.covered ? (
                  <>
                    <span className="font-medium">Non-recourse · </span>
                    Buyer is covered by {elig.cover.source === "allianz_cover" ? "Allianz Trade" : "the auto-underwriter"} up to {money(elig.cover.limit || 0)}.
                    If they default, the lender is made whole — your Trust Score is not affected.
                  </>
                ) : (
                  <>
                    <span className="font-medium">Buyer not covered for receivables financing. </span>
                    Allianz hasn't approved this buyer for cover, so factoring isn't available. This is decided automatically at buyer signup based on credit-risk signals — it's separate from the buyer's own credit line and can't be applied for here.
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={sell}
            disabled={busy || !elig?.eligible}
            data-testid="sell-invoice-submit"
            className="rounded-full bg-foreground text-background hover:bg-foreground/90">
            <Zap className="w-3.5 h-3.5 mr-1.5"/>
            {busy ? "Advancing…" : elig?.eligible ? `Get ${money(pricing?.advance_amount)} now` : "Not eligible"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
